import * as Sentry from '@sentry/browser';
import posthog from 'posthog-js';

export type DialogCallbackArg =
  | { message: string; origin: string | undefined }
  | { error: number };

// https://learn.microsoft.com/office/dev/add-ins/develop/dialog-handle-errors-events
const DIALOG_ERROR_MESSAGES: Record<number, string> = {
  // Raised after the dialog opens, via DialogEventReceived.
  12002:
    'No page exists at the dialog URL, or it loaded and then redirected to a page that cannot be found/loaded, or the URL syntax is invalid',
  12003:
    'Dialog was directed to a URL with the HTTP protocol (HTTPS is required)',
  12006:
    'Dialog was closed by the user, or the dialog page returned a Cross-Origin-Opener-Policy: same-origin response header',

  // Returned in the displayDialogAsync callback.
  12004:
    "Dialog URL domain isn't trusted (must be the same domain as the host page, including protocol and port)",
  12005: 'Dialog URL uses the HTTP protocol (HTTPS is required)',
  12007:
    'A dialog is already open from this host window (only one allowed at a time)',
  12009:
    'The user chose to ignore the dialog (declined to allow the add-in to open it)',
  12011:
    'The browser is blocking popups (e.g. Edge Legacy security zones, or Safari popup blocking)',
};

const IGNORED_DIALOG_ERROR_CODES = new Set([12006, 12009]);

export function logDialogError(errorCode: number) {
  const errorMessage =
    DIALOG_ERROR_MESSAGES[errorCode] ?? 'Unknown dialog error';

  posthog.capture('Login dialog failed', {
    isFailure: true,
    errorCode,
    errorMessage,
  });

  if (IGNORED_DIALOG_ERROR_CODES.has(errorCode)) return;

  Sentry.captureException(
    new Error(`Login dialog failed (${errorCode}): ${errorMessage}`),
  );
}

export function dialogErrorHandler(arg: DialogCallbackArg) {
  if ('error' in arg) logDialogError(arg.error);
}
